import { reset, stopSubmit } from 'redux-form';
import { all, call, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { appointmentsApiSelectors } from '.';
import i18n from '../../../i18n';
import type {
    IAnonymousAppointment,
    IAppointment,
    IAppointmentBranch,
    IAppointmentSettings,
    IContactRequest,
    ICustomerBase,
    IDownloadIcalRequest,
    IFetchRmTimeslotsRequest,
    IFetchTimeslotsRequest,
    IPerson,
    IRule,
    ITimeslot,
    ITopic,
} from '../../api/domain';
import type { AppointmentsApiService, I18ImportService } from '../../api/services';
import { FORMS, NotificationType } from '../../enum';
import { getTranslationSelector } from '../../utils/i18n-helpers';
import { scrollWindowToTop } from '../../utils/window';
import { addNotification, addNotificationClientAdvisory, hideProgressIndicator, showProgressIndicator } from '../application/application-actions';
import { resolveRmForCustomerBase } from '../customer-base-api/customer-base-api-actions';
import { customerMasterDataApiSelectors } from '../customer-master-data-api';
import { logError, logTrace } from '../logging-api/logging-api-actions';
import type { Action } from '../utils';
import {
    bookAppointment,
    bookAppointmentAnonymous,
    bookAppointmentAnonymousFailure,
    bookAppointmentAnonymousSuccess,
    bookAppointmentFailure,
    bookAppointmentSuccess,
    downloadICal,
    downloadICalFailure,
    downloadICalSuccess,
    fetchAllRelationshipManagerTimeSlotsForTopic,
    fetchBranches,
    fetchBranchesFailure,
    fetchBranchesSuccess,
    fetchRelationshipManagerTimeslots,
    fetchRelationshipManagerTimeslotsFailure,
    fetchRelationshipManagerTimeslotsSuccess,
    fetchRules,
    fetchRulesFailure,
    fetchRulesSuccess,
    fetchSettings,
    fetchSettingsFailure,
    fetchSettingsSuccess,
    fetchTimeslots,
    fetchTimeslotsFailure,
    fetchTimeslotsSuccess,
    fetchTopics,
    fetchTopicsFailure,
    fetchTopicsSuccess,
    selectCustomerBaseForAppointment,
    sendContactRequest,
    sendContactRequestFailure,
    sendContactRequestSuccess,
} from './appointments-api-actions';
import { getSettings } from './appointments-api-selectors';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* handleFetchTopics() {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        const topics: ITopic[] = yield call([api, api.fetchTopics]);
        yield put(fetchTopicsSuccess(topics));
    } catch (e: unknown) {
        yield put(logError(`handleFetchTopics failed with: ${e}`));
        yield put(fetchTopicsFailure());
    }
}

function* handleFetchTopicsSuccess(action: Action<ITopic[]>) {
    const { payload: topics } = action;
    const i18nImportService: I18ImportService = yield getContext('i18ImportService');
    i18nImportService.importTranslations({ 'appointments.topics': topics });
}

function* handleFetchBranches() {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        const branches: IAppointmentBranch[] = yield call([api, api.fetchBranches]);
        yield put(fetchBranchesSuccess(branches));
    } catch (e: unknown) {
        yield put(fetchBranchesFailure());
    }
}

function* handleFetchSettings() {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        const settings: IAppointmentSettings = yield call([api, api.fetchSettings]);
        yield put(fetchSettingsSuccess(settings));
    } catch (e: unknown) {
        yield put(fetchSettingsFailure());
        yield put(logError(`handleFetchSettings failed with: ${e}`));
    }
}

function* handleFetchTimeslots(action: Action<IFetchTimeslotsRequest>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        yield put(showProgressIndicator());
        const timeslots: ITimeslot[] = yield call([api, api.fetchTimeslots], action.payload);
        yield put(fetchTimeslotsSuccess(timeslots));
    } catch (e: unknown) {
        yield put(fetchTimeslotsFailure());
        yield put(logError(`handleFetchTimeslots failed with: ${e}`));
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleFetchRelationshipManagerTimeslots({ payload }: Action<IFetchRmTimeslotsRequest & IFetchTimeslotsRequest & { externalId: string }>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        yield put(showProgressIndicator());
        // This is a workaround for filtering the RM-Timeslots by the branchId as given by the triage list
        // Filtering RM-Timeslots by appointmentType, rmId and topicId is already implemented by the API
        const advisors: string[] = yield call([api, api.fetchAdvisorsByBranchAndTopic], { branchId: payload.branchId, topicId: payload.topicId });
        let timeslots: ITimeslot[] = [];
        // If the RM is not assigned to the branch, we return an empty array instead
        if (advisors.includes(payload.rmId)) {
            timeslots = yield call([api, api.fetchRelationshipManagerTimeslots], {
                appointmentType: payload.appointmentType,
                rmId: payload.rmId,
                topicId: payload.topicId,
            });
        }
        yield put(fetchRelationshipManagerTimeslotsSuccess(timeslots));
    } catch (e: unknown) {
        yield put(fetchRelationshipManagerTimeslotsFailure());
        yield put(logError(`handleFetchRelationshipManagerTimeslots failed with: ${e}`));
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleFetchAllRMTimeslotsForTopic({ payload: topicId }: Action<string>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        yield put(showProgressIndicator());
        const branches: IAppointmentBranch[] = yield select(appointmentsApiSelectors.getBranches) || [];
        const timeslots: ITimeslot[][] = yield all(branches.map((branch) => call([api, api.fetchTimeslots], { topicId, branchId: branch.id })));
        // filter "duplicate" time slots in the sense that there will probably be slots that overlap the same time
        // even if not "the same slot", we can display only one so we take the first and discard others
        const filteredTimeslots = timeslots.flat().filter((slot, index, slots) => {
            return slots.findIndex((s) => s.start === slot.start && s.end === slot.end) === index;
        });

        yield put(fetchRelationshipManagerTimeslotsSuccess(filteredTimeslots));
    } catch (e: unknown) {
        yield put(fetchRelationshipManagerTimeslotsFailure());
        yield put(logError(`handleFetchRelationshipManagerTimeslots failed with: ${e}`));
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleBookAppointment({ payload }: Action<IAppointment>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');

    const customer: IPerson = yield select(customerMasterDataApiSelectors.getCustomer);
    const customerBase: ICustomerBase = yield select(appointmentsApiSelectors.getSelectedCustomerBaseForAppointment);

    const metadata = (payload.metadata || []).concat(
        {
            category: 'customer',
            key: 'objectKey',
            value: customer.objectKey,
        },
        {
            category: 'customer',
            key: 'objectNumber',
            value: customer.objectKey,
        },
    );

    if (customerBase) {
        metadata.push(
            {
                category: 'customerBase',
                key: 'externalId',
                value: customerBase.externalId,
            },
            {
                category: 'customerBase',
                key: 'displayName',
                value: customerBase.displayName,
            },
        );
    }

    try {
        const appointment: IAppointment = {
            ...payload,
            metadata,
        };

        const bookedAppointment: IAppointment = yield call([api, api.bookAppointment], appointment);
        yield put(bookAppointmentSuccess(bookedAppointment));
    } catch (e: unknown) {
        yield put(bookAppointmentFailure());
        yield put(logError(`handleBookAppointment failed with: ${e}`));
    }
}

function* handleBookAppointmentFailure() {
    yield put(
        addNotification({
            title: i18n.t('appointments-api.book-appointment.notification.failure.title'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleBookAppointmentAnonymous(action: Action<IAnonymousAppointment>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        const bookedAppointment: IAnonymousAppointment = yield call([api, api.bookAnonymousAppointment], action.payload);
        yield put(bookAppointmentAnonymousSuccess(bookedAppointment));
    } catch (e: unknown) {
        yield put(bookAppointmentAnonymousFailure());
        yield put(logError(`handleBookAppointmentAnonymous failed with: ${e}`));
    }
}

function* handleDownloadIcal(action: Action<IDownloadIcalRequest>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        yield put(showProgressIndicator());
        const blob: Blob = yield call([api, api.downloadIcal], action.payload);
        const settings: IAppointmentSettings = yield select(getSettings);
        const translationSelector = getTranslationSelector(i18n.language);
        const filename = translationSelector(settings.l10nIcalFilename) || i18n.t('appointments-api.download-ical.filename');
        if ((window.navigator as any).msSaveOrOpenBlob) {
            // eslint-disable-next-line
            (window.navigator as any).msSaveOrOpenBlob(blob, filename);
        } else {
            const link: HTMLAnchorElement = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        yield put(downloadICalSuccess());
    } catch (error: unknown) {
        yield put(downloadICalFailure());
        yield put(logError(`Could not download iCal for appointment ${action.payload.appointmentId}: ${error}`));
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDownloadIcalFailure() {
    yield put(
        addNotification({
            title: i18n.t('appointments-api.download-ical.notification.failure.title'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleFetchRules() {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        const rules: IRule[] = yield call([api, api.fetchRules]);
        yield put(fetchRulesSuccess(rules));
    } catch (e: unknown) {
        yield put(fetchRulesFailure());
        yield put(logError(`handleFetchRules failed with: ${e}`));
    }
}

function* handleSendContactRequest(action: Action<IContactRequest>) {
    const api: AppointmentsApiService = yield getContext('appointmentsApiService');
    try {
        yield put(showProgressIndicator());
        yield call([api, api.sendContactRequest], action.payload);
        yield put(logTrace(`Contact request sent ${JSON.stringify(action.payload)}`));
        yield put(sendContactRequestSuccess());
        yield put(reset(FORMS.APPOINTMENT_FINDER_CONTACT_REQUEST));
    } catch (error: unknown) {
        yield put(sendContactRequestFailure());
        yield put(stopSubmit(FORMS.APPOINTMENT_FINDER_CONTACT_REQUEST, error as any));
        yield put(logError(`Could not send contact request : ${error} (request: ${JSON.stringify(action.payload)})`));
    } finally {
        yield put(hideProgressIndicator());
        yield scrollWindowToTop();
    }
}

function* handleSendContactRequestSuccess() {
    yield put(
        addNotification({
            title: i18n.t('appointments-api.send-contact-request.notification.success.title'),
            text: i18n.t('appointments-api.send-contact-request.notification.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleSendContactRequestFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'appointments-api.send-contact-request.notification.failure.title',
        }),
    );
}

function* handleSetCustomerBaseForAppointment({ payload: customerBase }: Action<ICustomerBase>) {
    yield put(resolveRmForCustomerBase(customerBase));
}

export function* appointmentsApiSaga() {
    yield all([
        call(addTranslations),
        takeEvery(fetchTopics, handleFetchTopics),
        takeEvery(fetchTopicsSuccess, handleFetchTopicsSuccess),
        takeEvery(fetchBranches, handleFetchBranches),
        takeEvery(fetchSettings, handleFetchSettings),
        takeEvery(fetchRules, handleFetchRules),
        takeEvery(fetchTimeslots, handleFetchTimeslots),
        takeEvery(fetchRelationshipManagerTimeslots, handleFetchRelationshipManagerTimeslots),
        takeEvery(fetchAllRelationshipManagerTimeSlotsForTopic, handleFetchAllRMTimeslotsForTopic),
        takeEvery(downloadICal, handleDownloadIcal),
        takeEvery(downloadICalFailure, handleDownloadIcalFailure),
        takeEvery(bookAppointment, handleBookAppointment),
        takeEvery(bookAppointmentFailure, handleBookAppointmentFailure),
        takeEvery(bookAppointmentAnonymous, handleBookAppointmentAnonymous),
        takeEvery(sendContactRequest, handleSendContactRequest),
        takeEvery(sendContactRequestSuccess, handleSendContactRequestSuccess),
        takeEvery(sendContactRequestFailure, handleSendContactRequestFailure),
        takeEvery(selectCustomerBaseForAppointment, handleSetCustomerBaseForAppointment),
    ]);
}
